import { useState, useEffect } from "react";
import { CalendarProvider } from "./calendar-context";
import CalendarHeader from "./calendar-header";
import DailyView from "./views/daily-view";
import WeeklyView from "./views/weekly-view";
import MonthlyView from "./views/monthly-view";
import { useToast } from "@/hooks/use-toast";
import type {
  ViewType,
  Event,
  TimeRange,
  BookedTimeRange,
} from "@/lib/calendar-types";
import { generateInitialEvents } from "@/lib/calendar-utils";

interface SimpleCalendarViewProps {
  initialBookedRanges: BookedTimeRange[];
  onTimeSlotSelect?: (timeRange: TimeRange) => void;
  slotDuration: number;
  selectedTimeSlots?: TimeRange[];
  onRangesChange?: (ranges: TimeRange[]) => void;
  onSlotClick?: (slot: Date) => void;
}

export default function SimpleCalendarView({
  initialBookedRanges,
  onTimeSlotSelect,
  slotDuration,
  selectedTimeSlots: externalSelectedTimeSlots,
  onRangesChange,
  onSlotClick,
}: SimpleCalendarViewProps) {
  const [view, setView] = useState<ViewType>("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<Event[]>([]);
  const [bookedRanges, setBookedRanges] =
    useState<BookedTimeRange[]>(initialBookedRanges);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<TimeRange[]>(
    externalSelectedTimeSlots || [],
  );
  const { toast } = useToast();

  useEffect(() => {
    // Load sample events for demonstration
    setEvents(generateInitialEvents());
  }, []);

  const handleViewChange = (newView: ViewType) => {
    setView(newView);
  };

  const handleDateChange = (date: Date) => {
    setCurrentDate(date);
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);

    if (view === "day") {
      newDate.setDate(newDate.getDate() - 1);
    } else if (view === "week") {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }

    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);

    if (view === "day") {
      newDate.setDate(newDate.getDate() + 1);
    } else if (view === "week") {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }

    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleEditEvent = (event: Event) => {
    // Not used in simplified view
  };

  const showConflictToast = (message: string) => {
    toast({
      title: "Booking Conflict",
      description: message,
      variant: "destructive",
    });
  };

  const handleAddTimeRange = (range: TimeRange) => {
    // In simplified view, we just pass the range to the parent
    if (onTimeSlotSelect) {
      onTimeSlotSelect(range);
    }

    // Update local state
    setSelectedTimeSlots([range]);

    // Notify parent component
    if (onRangesChange) {
      onRangesChange([range]);
    }
  };

  const handleRemoveTimeRange = (rangeId: string) => {
    // Remove the range from local state
    const updatedRanges = selectedTimeSlots.filter(
      (range) => range.id !== rangeId,
    );

    setSelectedTimeSlots(updatedRanges);

    // Notify parent component
    if (onRangesChange) {
      onRangesChange(updatedRanges);
    }
  };

  const handleClearAllSelections = () => {
    setSelectedTimeSlots([]);

    if (onRangesChange) {
      onRangesChange([]);
    }
  };

  // Update selectedTimeSlots when externalSelectedTimeSlots changes
  useEffect(() => {
    if (externalSelectedTimeSlots) {
      setSelectedTimeSlots([...externalSelectedTimeSlots]);
    }
  }, [externalSelectedTimeSlots]);

  return (
    <CalendarProvider
      value={{
        view,
        currentDate,
        events,
        selectedRanges: selectedTimeSlots,
        bookedRanges,
        selectionMode: "click-only",
        onViewChange: handleViewChange,
        onDateChange: handleDateChange,
        onEditEvent: handleEditEvent,
        onAddTimeRange: handleAddTimeRange,
        onRemoveTimeRange: handleRemoveTimeRange,
        onClearAllSelections: handleClearAllSelections,
        showConflictToast,
        onSlotClick,
      }}
    >
      <div className="border rounded-lg shadow bg-background">
        <CalendarHeader
          view={view}
          currentDate={currentDate}
          onViewChange={handleViewChange}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onToday={handleToday}
        />

        <div className="calendar-body">
          {view === "day" && <DailyView />}
          {view === "week" && <WeeklyView />}
          {view === "month" && <MonthlyView />}
        </div>
      </div>
    </CalendarProvider>
  );
}
